<script lang="ts">
    import { onMount, tick } from "svelte";
    import { getPathParams } from "svelte-mini-router";
    import { translate } from "@/utils/format";
    import { sendSlackCaloo } from "@/services/logging";
    import {
        resolvePartnerPortalSE,
        getReferralBasicInfo,
        type PartnerInfo,
        type PortalProduct,
        type PortalPremium,
    } from "@/services/referrals";
    import { Spinner } from "@/components";
    import { LANGUAGES, getUserLanguage, setUserLanguage } from "@/utils/lang";

    // -- Constants --
    const LS_EMAIL_KEY = "calooPartnerPortalEmail";
    const LS_EMAIL_TS_KEY = "calooPartnerPortalEmailAt";
    const LS_PARTNER_KEY_PREFIX = "calooPartnerPortalPartner:";
    const LS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
    const APP_STORE_URL = "https://apps.apple.com/app/caloo/id6446050968";
    const PLAY_STORE_URL =
        "https://play.google.com/store/apps/details?id=com.caloo.oorenji.app";

    // -- Types --
    type Step =
        | "loading"
        | "email"
        | "notRegistered"
        | "redirecting"
        | "portal"
        | "error";

    // -- State --
    let step = $state<Step>("loading");
    let referralId = $state("");
    let email = $state("");
    let errorMessage = $state("");
    let submitting = $state(false);

    let emailInput = $state<HTMLInputElement | null>(null);
    let didAutofocusEmail = $state(false);
    let currentLang = $state(getUserLanguage() || "es");

    let partner = $state<PartnerInfo | null>(null);
    let partnerDescription = $state<string>("");
    let products = $state<PortalProduct[]>([]);
    let benefits = $state<string[]>([]);
    let premium = $state<PortalPremium | null>(null);
    let userUid = $state("");
    let assignedNow = $state(false);
    let checkoutJustCompleted = $state(false);

    // -- Derived --
    const partnerName = $derived(partner?.name || referralId || "Partner");
    const partnerInitial = $derived(
        partnerName ? partnerName.charAt(0).toUpperCase() : "P",
    );
    const isPremiumActive = $derived(!!premium?.isPremium);
    const billingPortalUrl = $derived(premium?.billingPortalUrl || null);
    const hasSubscription = $derived(isPremiumActive || !!billingPortalUrl);

    $effect(() => {
        if (step !== "email") {
            didAutofocusEmail = false;
            return;
        }

        if (didAutofocusEmail) return;
        didAutofocusEmail = true;

        void (async () => {
            await tick();
            emailInput?.focus();
            emailInput?.select();
        })();
    });

    // -- Validation --
    function isValidEmail(v: string) {
        const s = String(v || "").trim();
        if (!s) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    }

    function isValidReferralId(v: string) {
        const s = String(v || "").trim();
        if (!s) return false;
        return /^[a-zA-Z0-9_-]{2,64}$/.test(s);
    }

    // -- localStorage helpers --
    function readSavedEmail(): string | null {
        try {
            const saved = localStorage.getItem(LS_EMAIL_KEY);
            const ts = localStorage.getItem(LS_EMAIL_TS_KEY);
            if (!saved || !ts) return null;
            if (Date.now() - Number(ts) > LS_TTL_MS) {
                clearSavedEmail();
                return null;
            }
            return saved;
        } catch {
            return null;
        }
    }

    function saveEmail(e: string) {
        try {
            localStorage.setItem(LS_EMAIL_KEY, e);
            localStorage.setItem(LS_EMAIL_TS_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
    }

    function clearSavedEmail() {
        try {
            localStorage.removeItem(LS_EMAIL_KEY);
            localStorage.removeItem(LS_EMAIL_TS_KEY);
        } catch {
            /* ignore */
        }
    }

    function partnerCacheKey(refId: string): string {
        return `${LS_PARTNER_KEY_PREFIX}${String(refId || "")
            .trim()
            .toLowerCase()}`;
    }

    function readSavedPartner(refId: string): PartnerInfo | null {
        try {
            const raw = localStorage.getItem(partnerCacheKey(refId));
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object") return null;
            const id = String((parsed as any).id || "").trim();
            const name = String((parsed as any).name || "").trim();
            if (!id || !name) return null;
            const logoUrl = String((parsed as any).logoUrl || "").trim();
            const tagline = String((parsed as any).tagline || "").trim();
            return {
                id,
                name,
                logoUrl: logoUrl || undefined,
                tagline: tagline || undefined,
            };
        } catch {
            return null;
        }
    }

    function savePartnerPreview(refId: string, p: PartnerInfo | null) {
        if (!p) return;
        try {
            const payload: PartnerInfo = {
                id: String(p.id || refId).trim(),
                name: String(p.name || "").trim() || String(refId).trim(),
                logoUrl: p.logoUrl ? String(p.logoUrl).trim() : undefined,
                tagline: p.tagline ? String(p.tagline).trim() : undefined,
            };
            localStorage.setItem(
                partnerCacheKey(refId),
                JSON.stringify(payload),
            );
        } catch {
            /* ignore */
        }
    }

    // -- Query params --
    function getQueryParam(name: string): string | null {
        try {
            const url = new URL(window.location.href);
            return url.searchParams.get(name);
        } catch {
            return null;
        }
    }

    function cleanUrlParams(...params: string[]) {
        try {
            const url = new URL(window.location.href);
            for (const p of params) url.searchParams.delete(p);
            window.history.replaceState({}, "", url.toString());
        } catch {
            /* ignore */
        }
    }

    // -- Actions --
    async function resolveEmail() {
        const trimmed = email.trim();

        if (!trimmed || !isValidEmail(trimmed)) {
            errorMessage = translate(
                "PP_ERROR_INVALID_EMAIL",
                "Introduce un email válido.",
            );
            step = "error";
            return;
        }

        step = "loading";
        submitting = true;

        try {
            const res = await resolvePartnerPortalSE(referralId, trimmed);

            if (!res.ok) {
                if (res.reason === "user_not_registered") {
                    step = "notRegistered";
                    return;
                }
                if (res.reason === "invalid_code") {
                    errorMessage = translate(
                        "PP_ERROR_INVALID_CODE",
                        "Este código de partner no es válido.",
                    );
                    step = "error";
                    return;
                }
                errorMessage =
                    String(res.reason || "").trim() ||
                    translate(
                        "PP_ERROR_GENERIC",
                        "Ha ocurrido un error. Inténtalo de nuevo.",
                    );
                step = "error";
                return;
            }

            // Server says user already belongs to another partner → redirect
            if (res.shouldRedirect && res.resolvedReferralId) {
                step = "redirecting";
                saveEmail(trimmed);
                setTimeout(() => {
                    window.location.href = `/${res.resolvedReferralId}?email=${encodeURIComponent(trimmed)}`;
                }, 1200);
                return;
            }

            // Populate state from single response
            saveEmail(trimmed);
            userUid = res.user?.uid || "";
            partner = res.partner || { id: referralId, name: referralId };
            savePartnerPreview(referralId, partner);
            premium = res.premium || null;
            products = res.products || [];
            benefits = res.benefits || [];
            assignedNow = !!res.assignedNow;

            // Slack notification when a user is freshly assigned
            if (assignedNow) {
                try {
                    const msg = `New Caloo user assigned to PARTNER ${referralId}: (id: ${userUid} – email: ${trimmed})`;
                    await sendSlackCaloo(userUid || "portal", msg);
                } catch {
                    /* non-blocking */
                }
            }

            step = "portal";
        } catch (err: any) {
            const data = err?.response?.data;
            const reason = String(data?.reason || "").trim();

            if (reason === "user_not_registered") {
                step = "notRegistered";
                return;
            }
            if (reason === "invalid_code") {
                errorMessage = translate(
                    "PP_ERROR_INVALID_CODE",
                    "Este código de partner no es válido.",
                );
                step = "error";
                return;
            }

            errorMessage =
                String(data?.message || err?.message || "").trim() ||
                translate(
                    "PP_ERROR_GENERIC",
                    "Ha ocurrido un error. Inténtalo de nuevo.",
                );
            step = "error";
        } finally {
            submitting = false;
        }
    }

    function changeEmail() {
        clearSavedEmail();
        email = "";
        assignedNow = false;
        checkoutJustCompleted = false;
        step = "email";
    }

    function retryFromError() {
        errorMessage = "";
        if (email && isValidEmail(email)) {
            resolveEmail();
        } else {
            step = "email";
        }
    }

    async function loadPartnerBasicInfo(refId: string) {
        try {
            const info = await getReferralBasicInfo(refId);
            if (info?.notFound) {
                // Keep a clear error for truly unknown partner codes.
                errorMessage = translate(
                    "PP_ERROR_INVALID_CODE",
                    "Este código de partner no es válido.",
                );
                step = "error";
                return;
            }

            if (!info?.ok) return;

            partnerDescription = String(info.partnerDescription || "").trim();
            partner = {
                id: refId,
                name: String(info.partnerName || refId).trim(),
                logoUrl: info.partnerLogoUrl || undefined,
                tagline: partnerDescription || undefined,
            };
            savePartnerPreview(refId, partner);
        } catch {
            // Non-blocking: keep fallback/cached partner
        }
    }

    // -- Lifecycle --
    onMount(async () => {
        const params = getPathParams();
        const fromPath = String(params?.["referralId"] || "").trim();
        const fromQuery = String(
            getQueryParam("CODE") || getQueryParam("code") || "",
        ).trim();
        const fromHistory = String(
            (window.history.state as any)?.CODE || "",
        ).trim();
        referralId = fromPath || fromQuery || fromHistory;

        if (!isValidReferralId(referralId)) {
            errorMessage = translate(
                "PP_ERROR_INVALID_CODE",
                "Este código de partner no es válido.",
            );
            step = "error";
            return;
        }

        // Best-effort partner preview (cached from previous visit)
        partner = readSavedPartner(referralId);

        // Load partner basic info (first-visit branding)
        await loadPartnerBasicInfo(referralId);

        // Detect post-checkout return
        const checkoutParam = getQueryParam("checkout");
        const sessionId = getQueryParam("session_id");
        if (checkoutParam === "success" || sessionId) {
            checkoutJustCompleted = true;
            cleanUrlParams("checkout", "session_id");
        }

        // Read email from query string or localStorage
        const queryEmail = getQueryParam("email");
        const savedEmail = readSavedEmail();
        const initialEmail = (queryEmail || savedEmail || "").trim();

        if (initialEmail && isValidEmail(initialEmail)) {
            email = initialEmail;
            if (queryEmail) cleanUrlParams("email");
            resolveEmail();
        } else {
            step = "email";
        }
    });
</script>

<section class="pp">
    {#if step === "loading"}
        <div class="pp__card pp__card--center">
            <Spinner text="" />
            <p class="pp__loadingText">
                {translate("PP_RESOLVING", "Verificando tu cuenta...")}
            </p>
        </div>
    {:else if step === "email"}
        <div class="pp__card">
            <header class="pp__brandHeader">
                <div class="pp__brand">
                    <div class="pp__brandGroup">
                        <img
                            class="pp__brandLogo pp__brandLogo--caloo"
                            src="/assets/img/logos/Isotipo.svg"
                            alt={translate("PP_BRAND_CALOO_ALT", "Caloo")}
                            loading="lazy"
                        />
                        <div class="pp__brandText">
                            <div class="pp__brandName">
                                {translate("PP_BRAND_CALOO_NAME", "Caloo")}
                            </div>
                            <div class="pp__brandSub">
                                {translate(
                                    "PP_BRAND_CALOO_SUBTITLE",
                                    "Nutrición de precisión",
                                )}
                            </div>
                        </div>
                    </div>

                    <div class="pp__brandSep" aria-hidden="true">×</div>

                    <div class="pp__brandGroup">
                        {#if partner?.logoUrl}
                            <img
                                class="pp__brandLogo pp__brandLogo--partner"
                                src={partner.logoUrl}
                                alt={partnerName}
                                loading="lazy"
                            />
                        {:else}
                            <div class="pp__brandFallback" aria-hidden="true">
                                {partnerInitial}
                            </div>
                        {/if}
                        <div class="pp__brandText">
                            <div class="pp__brandPartnerName">
                                {partnerName}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div class="pp__headline">
                <h1>
                    {translate("PP_JOIN_H1", "Únete a {partnerName} en Caloo", {
                        partnerName,
                    })}
                </h1>
                <p>
                    {translate(
                        "PP_JOIN_SUBTITLE",
                        "Activa tus ventajas exclusivas y optimiza tu nutrición con precisión",
                    )}
                </p>
            </div>

            <div class="pp__formCard">
                <div class="pp__form">
                    <label class="pp__label" for="pp-email">
                        {translate("PP_EMAIL_LABEL", "Tu email")}
                    </label>
                    <input
                        id="pp-email"
                        class="pp__input"
                        type="email"
                        inputmode="email"
                        autocomplete="email"
                        placeholder={translate(
                            "PP_EMAIL_PLACEHOLDER",
                            "tu@email.com",
                        )}
                        bind:this={emailInput}
                        bind:value={email}
                        disabled={submitting}
                        onkeydown={(e) => {
                            if (e.key === "Enter") void resolveEmail();
                        }}
                    />
                    <button
                        class="pp__btn pp__btn--primary pp__btn--cta"
                        type="button"
                        onclick={() => void resolveEmail()}
                        disabled={submitting}
                    >
                        {submitting
                            ? translate("PP_LOADING", "Cargando...")
                            : translate(
                                  "PP_JOIN_CTA",
                                  "Unirme a {partnerName}",
                                  {
                                      partnerName,
                                  },
                              )}
                    </button>

                    <p class="pp__socialProof">
                        {translate(
                            "PP_SOCIAL_PROOF",
                            "Miles de usuarios ya optimizan su nutrición con Caloo.",
                        )}
                    </p>
                </div>
            </div>

            <div class="pp__intro">
                <p>
                    {translate(
                        "PP_JOIN_DESC",
                        "Estás en la página oficial de {partnerName} en Caloo.\nAl unirte, tu cuenta quedará vinculada a su comunidad y podrás acceder a ventajas exclusivas, seguimiento completo y condiciones especiales dentro de la app.",
                        { partnerName },
                    )}
                </p>

                <ul class="pp__bullets">
                    <li>
                        {translate(
                            "PP_BENEFIT_1",
                            "Plan nutricional personalizado según tu objetivo",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_BENEFIT_2",
                            "Seguimiento completo de tu progreso y evolución",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_BENEFIT_3",
                            "Registro de comidas por foto o voz en segundos",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_BENEFIT_4",
                            "Edición de ingredientes y cantidades en un clic",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_BENEFIT_5",
                            "Integración con actividad (Apple Health / Health Connect, Strava, Garmin)",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_BENEFIT_6",
                            "Acceso a ventajas exclusivas para miembros de {partnerName}",
                            { partnerName },
                        )}
                    </li>
                </ul>

                <p class="pp__miniTitle">
                    {translate(
                        "PP_SCREENSHOTS_TITLE",
                        "Todo tu progreso, nutrición y evolución en un solo lugar",
                    )}
                </p>

                <img
                    class="pp__hero"
                    src="/assets/img/content/screens.png"
                    alt={translate(
                        "PP_EMAIL_HERO_ALT",
                        "Caloo: salud y nutrición",
                    )}
                    loading="lazy"
                />
            </div>

            <p class="pp__security">
                {translate(
                    "PP_SECURITY_NOTE",
                    "Tu email se utiliza únicamente para vincular tu cuenta a la comunidad de {partnerName}.\nNo enviamos spam ni compartimos tus datos. Puedes cancelar en cualquier momento.",
                    { partnerName },
                )}
            </p>

            <div class="pp__steps">
                <h3>{translate("PP_HOW_IT_WORKS", "CÓMO FUNCIONA")}</h3>
                <ol>
                    <li>
                        <span class="pp__stepNum">1</span>
                        {translate("PP_JOIN_STEP_1", "Introduce tu email")}
                    </li>
                    <li>
                        <span class="pp__stepNum">2</span>
                        {translate(
                            "PP_JOIN_STEP_2",
                            "Vincula tu cuenta a {partnerName}",
                            { partnerName },
                        )}
                    </li>
                    <li>
                        <span class="pp__stepNum">3</span>
                        {translate(
                            "PP_JOIN_STEP_3",
                            "Activa tus ventajas dentro de Caloo",
                        )}
                    </li>
                </ol>
            </div>
        </div>
    {:else if step === "notRegistered"}
        <div class="pp__card">
            <div class="pp__notReg">
                <img
                    class="pp__notRegHero"
                    src="/assets/img/content/iphone.png"
                    alt={translate(
                        "PP_NOT_REGISTERED_HERO_ALT",
                        "Descarga Caloo para empezar",
                    )}
                    loading="lazy"
                />
                <div class="pp__notRegIcon" aria-hidden="true"></div>
                <h2>
                    {translate(
                        "PP_NOT_REGISTERED_TITLE",
                        "¡Descarga la app primero!",
                    )}
                </h2>
                <p>
                    {translate(
                        "PP_NOT_REGISTERED_DESC",
                        "Para acceder a las ofertas de {partnerName}, necesitas una cuenta en Caloo. Descarga la app, regístrate y vuelve a este enlace.",
                        { partnerName },
                    )}
                </p>

                <ul class="pp__bullets">
                    <li>
                        {translate(
                            "PP_NOT_REGISTERED_B1",
                            "Es el primer paso para activar tu ventaja con el partner",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_NOT_REGISTERED_B2",
                            "Dentro de la app podrás ver tu Premium y tu plan",
                        )}
                    </li>
                    <li>
                        {translate(
                            "PP_NOT_REGISTERED_B3",
                            "Vuelve aquí después para completar la activación",
                        )}
                    </li>
                </ul>

                <div class="pp__stores">
                    <a
                        class="pp__storeBtn pp__storeBtn--apple"
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            ><path
                                d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
                            /></svg
                        >
                        {translate("PP_DOWNLOAD_IOS", "App Store")}
                    </a>
                    <a
                        class="pp__storeBtn pp__storeBtn--google"
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            ><path
                                d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m.91-.91L19.59 12l-1.87-2.21-2.27 2.27 2.27 2.15M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"
                            /></svg
                        >
                        {translate("PP_DOWNLOAD_ANDROID", "Google Play")}
                    </a>
                </div>

                <details class="pp__faq">
                    <summary>
                        {translate(
                            "PP_NOT_REGISTERED_WHY_TITLE",
                            "¿Por qué necesito la app?",
                        )}
                    </summary>
                    <p>
                        {translate(
                            "PP_NOT_REGISTERED_WHY_DESC",
                            "Caloo es tu app de salud y nutrición personalizada. Tu suscripción se activa directamente en la app.",
                        )}
                    </p>
                </details>

                <button
                    class="pp__btn pp__btn--ghost"
                    type="button"
                    onclick={changeEmail}
                >
                    {translate(
                        "PP_TRY_ANOTHER_EMAIL",
                        "Intentar con otro email",
                    )}
                </button>
            </div>
        </div>
    {:else if step === "redirecting"}
        <div class="pp__card pp__card--center">
            <Spinner text="" />
            <p class="pp__loadingText">
                {translate(
                    "PP_REDIRECTING",
                    "Redirigiendo al partner correcto...",
                )}
            </p>
        </div>
    {:else if step === "portal"}
        <div class="pp__card">
            <header class="pp__header">
                {#if partner?.logoUrl}
                    <img
                        class="pp__logo"
                        src={partner.logoUrl}
                        alt={partnerName}
                    />
                {:else}
                    <div class="pp__mark" aria-hidden="true">
                        {partnerInitial}
                    </div>
                {/if}
                <div class="pp__headerText">
                    <h1>{partnerName}</h1>
                    {#if partner?.tagline}
                        <p>{partner.tagline}</p>
                    {:else}
                        <p>
                            {translate(
                                "PP_SUBTITLE_WITH_PARTNER",
                                "Oferta exclusiva para miembros de {partnerName}",
                                { partnerName },
                            )}
                        </p>
                    {/if}
                </div>
            </header>

            {#if checkoutJustCompleted}
                <div class="pp__banner pp__banner--success">
                    {translate(
                        "PP_CHECKOUT_SUCCESS",
                        "¡Compra completada! Abre la app para activar tu Premium.",
                    )}
                </div>
            {/if}

            <div class="pp__identity">
                <div class="pp__identityLabel">
                    {translate("PP_EMAIL_LABEL", "Tu email")}
                </div>
                <div class="pp__identityValue">{email}</div>
                <button
                    class="pp__changeEmail"
                    type="button"
                    onclick={changeEmail}
                >
                    {translate("PP_CHANGE_EMAIL", "Cambiar email")}
                </button>
            </div>

            {#if assignedNow && !checkoutJustCompleted}
                <div class="pp__banner pp__banner--info">
                    {translate(
                        "PP_ASSIGNED_NOW",
                        "¡Listo! Te hemos vinculado a {partnerName}.",
                        { partnerName },
                    )}
                </div>
            {/if}

            {#if isPremiumActive}
                <div class="pp__status pp__status--active">
                    {translate(
                        "PP_STATUS_ACTIVE",
                        "Ya tienes Premium activo ✓",
                    )}
                </div>
            {/if}

            {#if products.length > 0 && !isPremiumActive}
                <h3 class="pp__sectionTitle">
                    {translate("PP_STEP_2", "Elige tu plan")}
                </h3>
                <div class="pp__plans">
                    {#each products as product (product.id)}
                        <div
                            class="pp__plan"
                            class:pp__plan--recommended={product.recommended}
                        >
                            {#if product.recommended}
                                <span class="pp__badge">
                                    {translate("PP_BEST_PRICE", "Mejor precio")}
                                </span>
                            {:else if product.badge}
                                <span class="pp__badge">{product.badge}</span>
                            {/if}

                            {#if product.image}
                                <img
                                    class="pp__planImage"
                                    src={product.image}
                                    alt={product.title}
                                    loading="lazy"
                                />
                            {/if}
                            <div class="pp__planName">{product.title}</div>
                            <div class="pp__planPrice">
                                <span class="pp__planAmount"
                                    >{product.priceLabel}</span
                                >
                            </div>
                            {#if product.text}
                                <div class="pp__planDesc">
                                    {product.text}
                                </div>
                            {/if}
                            <a
                                class="pp__btn pp__btn--primary pp__btn--full"
                                href={product.url}
                                rel="noopener noreferrer"
                            >
                                {translate("PP_BUY", "Comprar")}
                            </a>
                        </div>
                    {/each}
                </div>
            {/if}

            {#if benefits.length > 0}
                <div class="pp__benefits">
                    <h3>
                        {translate("PP_BENEFITS_TITLE", "Incluido en Premium")}
                    </h3>
                    <ul>
                        {#each benefits as b}
                            <li>{b}</li>
                        {/each}
                    </ul>
                </div>
            {/if}

            <div class="pp__actions">
                {#if billingPortalUrl}
                    <a
                        class="pp__btn pp__btn--secondary"
                        href={billingPortalUrl}
                        rel="noopener noreferrer"
                    >
                        {translate(
                            "PP_MANAGE_SUBSCRIPTION",
                            "Gestionar suscripción",
                        )}
                    </a>
                {/if}
            </div>
        </div>
    {:else if step === "error"}
        <div class="pp__card pp__card--center">
            <div class="pp__errorIcon" aria-hidden="true">!</div>
            <p class="pp__errorMsg">{errorMessage}</p>
            <div class="pp__errorActions">
                <button
                    class="pp__btn pp__btn--primary"
                    type="button"
                    onclick={retryFromError}
                >
                    {translate("PP_RETRY", "Reintentar")}
                </button>
                <button
                    class="pp__btn pp__btn--ghost"
                    type="button"
                    onclick={changeEmail}
                >
                    {translate("PP_CHANGE_EMAIL", "Cambiar email")}
                </button>
            </div>
        </div>
    {/if}

    <footer class="pp__footer">
        <div class="pp__langBar">
            {#each LANGUAGES as lang (lang.code)}
                <button
                    class="pp__langPill"
                    class:pp__langPill--active={currentLang === lang.code}
                    type="button"
                    onclick={() => {
                        currentLang = lang.code;
                        setUserLanguage(lang.code);
                        location.reload();
                    }}
                    aria-label={lang.label}
                    title={lang.label}
                >
                    <img
                        src={lang.flag}
                        alt={lang.label}
                        width="20"
                        height="20"
                    />
                </button>
            {/each}
        </div>
        <span>{translate("PP_FOOTER", "partners.caloo.app")}</span>
    </footer>
</section>

<style lang="scss">
    /* ── Layout ── */
    .pp {
        min-height: 100vh;
        background: var(--backgroundMain);
        color: var(--textColor);
        display: grid;
        align-content: start;
        justify-items: center;
        padding: 24px 16px;
        gap: 16px;
        font-family: "Montserrat", sans-serif;
    }

    /* ── Card ── */
    .pp__card {
        width: min(520px, 100%);
        background: var(--white);
        border: 1px solid var(--border);
        border-radius: 18px;
        padding: 24px 20px;
        box-shadow: var(--cardShadow);
        display: grid;
        gap: 18px;
    }

    .pp__card--center {
        justify-items: center;
        text-align: center;
        padding: 48px 20px;
    }

    /* ── Header ── */
    .pp__brandHeader {
        padding: 12px 14px;
        border: 1px solid var(--border);
        border-radius: 16px;
        background: var(--backgroundMain);
    }

    .pp__brand {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
    }

    .pp__brandGroup {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .pp__brandSep {
        font-weight: 900;
        color: var(--subtleText);
        opacity: 0.9;
    }

    .pp__brandLogo {
        width: 38px;
        height: 38px;
        object-fit: contain;
        flex-shrink: 0;
    }

    .pp__brandLogo--partner {
        border-radius: 12px;
        object-fit: cover;
        border: 1px solid var(--border);
        background: var(--white);
    }

    .pp__brandFallback {
        width: 38px;
        height: 38px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        background: rgba(76, 227, 157, 0.12);
        border: 1px solid rgba(76, 227, 157, 0.28);
        color: var(--primary);
        font-weight: 900;
        font-size: 16px;
        flex-shrink: 0;
    }

    .pp__brandText {
        display: grid;
        gap: 2px;
        min-width: 0;
    }

    .pp__brandName {
        font-size: 14px;
        font-weight: 900;
        color: var(--black);
        line-height: 1.1;
    }

    .pp__brandPartnerName {
        font-size: 13px;
        font-weight: 900;
        color: var(--black);
        line-height: 1.1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 220px;
    }

    .pp__brandSub {
        font-size: 12px;
        color: var(--subtleText);
        line-height: 1.25;
    }

    .pp__headline {
        display: grid;
        gap: 6px;
        margin-top: 2px;
    }

    .pp__headline h1 {
        margin: 0;
        font-size: 22px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.18;
    }

    .pp__headline p {
        margin: 0;
        font-size: 13px;
        color: var(--subtleText);
        line-height: 1.45;
    }

    /* ── Portal header (existing flow) ── */
    .pp__header {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 14px;
        align-items: center;
    }

    .pp__mark {
        width: 48px;
        height: 48px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        background: rgba(76, 227, 157, 0.12);
        border: 1px solid rgba(76, 227, 157, 0.28);
        color: var(--primary);
        font-weight: 900;
        font-size: 20px;
        flex-shrink: 0;
    }

    .pp__logo {
        width: 48px;
        height: 48px;
        border-radius: 14px;
        object-fit: cover;
        border: 1px solid var(--border);
    }

    .pp__headerText h1 {
        margin: 0;
        font-size: 18px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.01em;
        line-height: 1.25;
    }

    .pp__headerText p {
        margin: 4px 0 0;
        font-size: 13px;
        color: var(--subtleText);
        line-height: 1.35;
    }

    /* ── Intro / conversion copy ── */
    .pp__intro {
        display: grid;
        gap: 10px;
    }

    .pp__intro p {
        margin: 0;
        font-size: 13px;
        color: var(--subtleText);
        line-height: 1.45;
        white-space: pre-line;
    }

    .pp__bullets {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 8px;
    }

    .pp__bullets li {
        font-size: 13px;
        color: var(--textColor);
        line-height: 1.4;
        display: flex;
        gap: 10px;
        align-items: flex-start;
    }

    .pp__bullets li::before {
        content: "\2713";
        color: var(--primary);
        font-weight: 900;
        line-height: 1.2;
        margin-top: 1px;
    }

    .pp__hero {
        width: 100%;
        aspect-ratio: 16 / 7;
        object-fit: cover;
        border-radius: 14px;
        border: 1px solid var(--border);
        background: var(--backgroundMain);
    }

    .pp__miniTitle {
        margin: 10px 0 10px !important;
        font-size: 12px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: 0.01em;
    }

    .pp__security {
        border: 1px solid var(--border);
        border-radius: 14px;
        padding: 12px 14px;
        background: var(--backgroundMain);
        font-size: 12px;
        color: var(--subtleText);
        line-height: 1.45;
        white-space: pre-line;
    }

    /* ── How-it-works steps ── */
    .pp__steps {
        padding: 14px 16px;
        border: 1px solid var(--border);
        border-radius: 14px;
        background: var(--backgroundMain);
    }

    .pp__steps h3 {
        margin: 0 0 10px;
        font-size: 13px;
        font-weight: 800;
        color: var(--black);
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    .pp__steps ol {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 8px;
    }

    .pp__steps li {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 13px;
        color: var(--textColor);
        line-height: 1.35;
    }

    .pp__stepNum {
        width: 24px;
        height: 24px;
        border-radius: 999px;
        background: var(--primary);
        color: #12121f;
        display: grid;
        place-items: center;
        font-size: 12px;
        font-weight: 900;
        flex-shrink: 0;
    }

    /* ── Form ── */
    .pp__form {
        display: grid;
        gap: 14px;
    }

    .pp__formCard {
        padding: 20px;
        border: 1px solid rgba(76, 227, 157, 0.15);
        border-radius: 20px;
        background: linear-gradient(
            168deg,
            rgba(76, 227, 157, 0.06) 0%,
            transparent 60%
        );
        box-shadow:
            0 2px 12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(76, 227, 157, 0.06);
    }

    .pp__label {
        font-size: 13px;
        font-weight: 700;
        color: var(--black);
        letter-spacing: -0.01em;
    }

    .pp__input {
        width: 100%;
        padding: 15px 16px;
        border-radius: 12px;
        border: 1px solid var(--border);
        background: var(--white);
        color: var(--black);
        font-size: 16px;
        font-family: inherit;
        transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        box-sizing: border-box;
    }

    .pp__input::placeholder {
        color: var(--subtleText);
        opacity: 0.5;
    }

    .pp__input:focus-visible {
        outline: none;
        border-color: var(--primary);
        box-shadow:
            0 0 0 4px rgba(76, 227, 157, 0.12),
            0 1px 3px rgba(0, 0, 0, 0.06);
    }

    /* ── Buttons ── */
    .pp__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: none;
        border-radius: 14px;
        padding: 13px 20px;
        font-size: 14px;
        font-weight: 800;
        font-family: inherit;
        cursor: pointer;
        transition:
            opacity 0.15s,
            transform 0.1s;
    }

    .pp__btn:active:not(:disabled) {
        transform: scale(0.97);
    }

    .pp__btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    .pp__btn--primary {
        background: var(--primary);
        color: #12121f;
    }

    .pp__btn--cta {
        width: 100%;
        padding: 16px 24px !important;
        font-size: 14px;
        font-weight: 800;
        letter-spacing: -0.01em;
        border-radius: 14px;
        background: linear-gradient(180deg, #5aeaa0 0%, var(--primary) 100%);
        box-shadow:
            0 4px 14px rgba(76, 227, 157, 0.25),
            0 1px 3px rgba(0, 0, 0, 0.08);
        transition:
            opacity 0.15s,
            transform 0.12s ease,
            box-shadow 0.2s ease;
    }

    .pp__btn--cta:hover:not(:disabled) {
        box-shadow:
            0 6px 20px rgba(76, 227, 157, 0.3),
            0 2px 6px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
    }

    .pp__btn--cta:active:not(:disabled) {
        transform: scale(0.98) translateY(0);
        box-shadow: 0 2px 8px rgba(76, 227, 157, 0.2);
    }

    .pp__socialProof {
        margin: 4px 0 0;
        font-size: 12px;
        color: var(--subtleText);
        text-align: center;
        line-height: 1.4;
        opacity: 0.7;
    }

    .pp__btn--primary:hover:not(:disabled) {
        background: var(--primaryDim);
    }

    .pp__btn--secondary {
        background: transparent;
        border: 1px solid var(--primary);
        color: var(--primary);
    }

    .pp__btn--secondary:hover:not(:disabled) {
        background: rgba(76, 227, 157, 0.08);
    }

    .pp__btn--ghost {
        background: transparent;
        color: var(--subtleText);
        padding: 10px 16px;
        font-size: 13px;
    }

    .pp__btn--ghost:hover:not(:disabled) {
        color: var(--black);
    }

    .pp__btn--full {
        width: 100%;
    }

    /* ── Not-Registered ── */
    .pp__notReg {
        display: grid;
        gap: 12px;
        text-align: center;
        justify-items: center;
    }

    .pp__notRegHero {
        width: 100%;
        max-width: 420px;
        aspect-ratio: 16 / 9;
        object-fit: cover;
        border-radius: 14px;
        border: 1px solid var(--border);
    }

    .pp__notRegIcon {
        font-size: 48px;
        line-height: 1;
    }

    .pp__notReg h2 {
        margin: 0;
        font-size: 20px;
        font-weight: 900;
        color: var(--black);
    }

    .pp__notReg > p {
        margin: 0;
        font-size: 14px;
        color: var(--subtleText);
        line-height: 1.45;
        max-width: 380px;
    }

    .pp__stores {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        justify-content: center;
        margin-top: 6px;
    }

    .pp__storeBtn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        border-radius: 14px;
        font-size: 14px;
        font-weight: 800;
        text-decoration: none;
        transition: opacity 0.15s;
    }

    .pp__storeBtn:hover {
        opacity: 0.85;
    }

    .pp__storeBtn--apple {
        background: var(--black);
        color: var(--backgroundMain);
    }

    .pp__storeBtn--google {
        background: var(--primary);
        color: #12121f;
    }

    .pp__faq {
        width: 100%;
        max-width: 400px;
        text-align: left;
        border: 1px solid var(--border);
        border-radius: 14px;
        padding: 12px 14px;
        margin-top: 6px;
    }

    .pp__faq summary {
        font-size: 13px;
        font-weight: 800;
        color: var(--black);
        cursor: pointer;
        list-style: none;
    }

    .pp__faq summary::-webkit-details-marker {
        display: none;
    }

    .pp__faq p {
        margin: 8px 0 0;
        font-size: 13px;
        color: var(--subtleText);
        line-height: 1.45;
    }

    /* ── Loading text ── */
    .pp__loadingText {
        margin: 12px 0 0;
        font-size: 14px;
        color: var(--subtleText);
    }

    /* ── Banners ── */
    .pp__banner {
        border-radius: 14px;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.35;
        text-align: center;
    }

    .pp__banner--success {
        background: rgba(76, 227, 157, 0.1);
        border: 1px solid rgba(76, 227, 157, 0.35);
        color: var(--primary);
    }

    .pp__banner--info {
        background: rgba(76, 227, 157, 0.06);
        border: 1px solid rgba(76, 227, 157, 0.2);
        color: var(--primary);
    }

    /* ── Identity (email) ── */
    .pp__identity {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 10px;
        padding: 12px 14px;
        border: 1px solid var(--border);
        border-radius: 14px;
        background: var(--backgroundMain);
    }

    .pp__identityLabel {
        font-size: 11px;
        font-weight: 900;
        color: var(--subtleText);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
    }

    .pp__identityValue {
        font-size: 13px;
        font-weight: 800;
        color: var(--black);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
    }

    .pp__changeEmail {
        background: transparent;
        border: none;
        padding: 6px 8px;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 900;
        color: var(--primary);
        cursor: pointer;
        transition: background 0.15s;
        white-space: nowrap;
    }

    .pp__changeEmail:hover {
        background: rgba(76, 227, 157, 0.1);
    }

    .pp__changeEmail:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba(76, 227, 157, 0.18);
    }

    /* ── Status badges ── */
    .pp__status {
        border-radius: 14px;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 700;
        text-align: center;
    }

    .pp__status--active {
        background: rgba(76, 227, 157, 0.1);
        border: 1px solid rgba(76, 227, 157, 0.3);
        color: var(--success, var(--primary));
    }

    .pp__sectionTitle {
        margin: 2px 0 0;
        font-size: 13px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.01em;
    }

    /* ── Plan cards ── */
    .pp__plans {
        display: grid;
        gap: 14px;
    }

    .pp__plan {
        position: relative;
        border: 1px solid var(--border);
        border-radius: 16px;
        padding: 18px 18px 16px;
        background: var(--backgroundMain);
        display: grid;
        gap: 8px;
        transition:
            border-color 0.15s,
            transform 0.12s,
            box-shadow 0.15s;
    }

    .pp__plan:hover {
        border-color: rgba(76, 227, 157, 0.35);
        transform: translateY(-1px);
    }

    .pp__plan:focus-within {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(76, 227, 157, 0.14);
    }

    .pp__plan--recommended {
        border-color: var(--primary);
        box-shadow: 0 0 0 1px var(--primary);
    }

    .pp__badge {
        position: absolute;
        top: 12px;
        right: 12px;
        background: var(--primary);
        color: #12121f;
        font-size: 11px;
        font-weight: 900;
        padding: 4px 10px;
        border-radius: 999px;
        letter-spacing: 0.02em;
        z-index: 1;
    }

    .pp__planImage {
        width: 100%;
        aspect-ratio: 16 / 7;
        object-fit: cover;
        border-radius: 14px;
        border: 1px solid var(--border);
        margin-top: 6px;
    }

    .pp__planName {
        font-size: 17px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.01em;
        line-height: 1.25;
    }

    .pp__planPrice {
        display: flex;
        align-items: baseline;
        gap: 4px;
    }

    .pp__planAmount {
        font-size: 26px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.1;
    }

    .pp__planDesc {
        font-size: 13px;
        color: var(--subtleText);
        line-height: 1.4;
        margin-top: 2px;
        margin-bottom: 6px;
    }

    /* ── Benefits ── */
    .pp__benefits {
        padding: 14px 16px;
        border: 1px solid var(--border);
        border-radius: 14px;
        background: var(--backgroundMain);
    }

    .pp__benefits h3 {
        margin: 0 0 10px;
        font-size: 13px;
        font-weight: 800;
        color: var(--black);
    }

    .pp__benefits ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 6px;
    }

    .pp__benefits li {
        font-size: 13px;
        color: var(--textColor);
        line-height: 1.35;
    }

    .pp__benefits li::before {
        content: "\2713  ";
        color: var(--primary);
        font-weight: 900;
    }

    /* ── Actions ── */
    .pp__actions {
        display: grid;
        gap: 8px;
        justify-items: center;
    }

    /* ── Error ── */
    .pp__errorIcon {
        font-size: 48px;
        line-height: 1;
    }

    .pp__errorMsg {
        margin: 8px 0 0;
        font-size: 15px;
        color: var(--textColor);
        line-height: 1.45;
        max-width: 380px;
        text-align: center;
    }

    .pp__errorActions {
        display: grid;
        gap: 8px;
        justify-items: center;
        margin-top: 8px;
    }

    /* ── Footer ── */
    .pp__footer {
        font-size: 12px;
        color: var(--subtleText);
        display: grid;
        gap: 12px;
        justify-items: center;
    }

    .pp__langBar {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        justify-content: center;
    }

    .pp__langPill {
        display: grid;
        place-items: center;
        width: 36px;
        height: 36px;
        padding: 0px !important;
        border: 1.5px solid transparent;
        border-radius: 999px;
        background: var(--white);
        cursor: pointer;
        transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            transform 0.15s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .pp__langPill img {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        object-fit: cover;
        pointer-events: none;
    }

    .pp__langPill:hover {
        border-color: rgba(76, 227, 157, 0.3);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transform: translateY(-1px);
    }

    .pp__langPill--active {
        border-color: var(--primary);
        box-shadow:
            0 0 0 3px rgba(76, 227, 157, 0.12),
            0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .pp__langPill--active:hover {
        transform: none;
    }
</style>
